import { postSession } from "@/app/providers/services";
import { DecisionGraph, decisionGraphRelease, graphToDecisionGraph } from "@imminently/decisively-core";
import { legacyGraphWithRelease } from "./legacyGraph";
import React, { useEffect, useMemo, useState } from "react";

/**
 * The session modal's graph: the session's saved decision graph read as a DecisionGraph (nothing built up front, no
 * re-solve). Session views read it from SessionGraphContext; ParsedGraphContext keeps the release's rule graph for
 * the release components (rule editor, attribute forms) rendered inside the modal.
 */
export const SessionGraphContext = React.createContext<DecisionGraph | null>(null);

/** The session modal's DecisionGraph, or null outside the modal / while it loads. */
export const useSessionDecisionGraph = () => React.useContext(SessionGraphContext);

const solveSession = (session: any, release: any, response: any[]) =>
  postSession.submit({
    params: { model: release.model, release: release.id },
    query: { session: session.id } as any,
    body: { force: true, save: false, data: {}, response } as any,
  });

/**
 * The session's DecisionGraph: its saved decision graph; for older sessions their legacy graph (built with the rule
 * graph as before, then read as a DecisionGraph); only a session with no graph at all is solved to get one.
 */
export const useSessionGraph = (
  session: any,
  release: any,
  ruleGraph: DecisionGraph,
): [DecisionGraph | null, string | null, string | null] => {
  const stored = session?.decisionGraph;
  const legacy = session?.graph;
  const releaseRuleData = useMemo(() => (release ? decisionGraphRelease(release) : undefined), [release]);

  const saved = useMemo((): { graph: DecisionGraph | null; error: string | null } => {
    if (!release) return { graph: null, error: null };
    try {
      if (stored) return { graph: DecisionGraph.from(stored, { release: releaseRuleData }), error: null };
      if (legacy && !(legacy instanceof DecisionGraph)) {
        const noEdges = typeof legacy === "object" && (legacy as any).value?.noEdges === true;
        // with noEdges the graph is read as it is; otherwise as the legacy graph was built with the rule graph
        const graph = noEdges ? graphToDecisionGraph(legacy, { release, session: true }) : legacyGraphWithRelease(legacy, ruleGraph);
        return { graph, error: null };
      }
    } catch (e) {
      console.error("Failed to read session graph", e);
      return { graph: null, error: "Failed to build graph" };
    }
    return { graph: null, error: null };
  }, [stored, legacy, release, releaseRuleData, ruleGraph]);

  // a session with no graph saved: solve it (without saving) for one
  const [fetched, setFetched] = useState<{ key: string; graph: DecisionGraph | null; error?: string } | null>(null);
  const fetchKey = session?.id && release?.id ? `${session.id}:${session.lastModified}:${release.id}` : null;
  const needsFetch = !stored && !legacy && fetchKey !== null;
  useEffect(() => {
    if (!needsFetch || fetched?.key === fetchKey) return;
    let cancelled = false;
    solveSession(session, release, [{ type: "decisionGraph", debug: true }])
      .then((res: any) => {
        const decisionGraph = res.data?.decisionGraph;
        if (!cancelled) {
          setFetched({
            key: fetchKey!,
            graph: decisionGraph ? DecisionGraph.from(decisionGraph, { release: releaseRuleData }) : null,
            ...(decisionGraph ? {} : { error: "This session has no decision results" }),
          });
        }
      })
      .catch((e: unknown) => {
        console.error("Failed to fetch session graph", e);
        if (!cancelled) setFetched({ key: fetchKey!, graph: null, error: "Couldn't load this session's decision results" });
      });
    return () => {
      cancelled = true;
    };
  }, [needsFetch, fetchKey]);

  const current = fetched?.key === fetchKey ? fetched : null;
  // the third value: the graph couldn't be had (shown in the tabs instead of a loading state)
  return [saved.graph ?? current?.graph ?? null, saved.error, needsFetch ? (current?.error ?? null) : null];
};

export type SessionValidations = { validations?: any[]; loading: boolean; error?: string };

/**
 * The session's validations, for the validation overlay. Sessions keep their last solve's shown validations; an
 * older session without them asks decisionapi (no graph in the response, nothing saved) while the overlay is mounted.
 */
export const useSessionValidations = (session: any, release: any): SessionValidations => {
  const saved = Array.isArray(session?.validations) ? session.validations : undefined;
  const [fetched, setFetched] = useState<{ key: string; validations?: any[]; error?: string } | null>(null);
  const key =
    !saved && session?.id && release?.id && release?.model ? `${session.id}:${session.lastModified}:${release.id}` : null;
  useEffect(() => {
    if (!key || fetched?.key === key) return;
    let cancelled = false;
    solveSession(session, release, [])
      .then((res: any) => {
        if (!cancelled) setFetched({ key, validations: res.data?.validations ?? [] });
      })
      .catch((e: unknown) => {
        console.error("Failed to fetch session validations", e);
        if (!cancelled) setFetched({ key, error: "Couldn't check validations" });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);
  if (saved) return { validations: saved, loading: false };
  if (!key) return { loading: false };
  const current = fetched?.key === key ? fetched : null;
  return current ? { validations: current.validations, loading: false, error: current.error } : { loading: true };
};
